import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareHeart, Star, ThumbsUp, ThumbsDown, Download, Lightbulb, Flame, Send, Loader2 } from "lucide-react";
import { api, fmtDateTime, apiError } from "@/lib/api";
import { Kpi, Panel, RankTable, Donut, Bars } from "@/lib/adminUI";
import { exportCsv } from "@/lib/exportCsv";
import { Skeleton } from "@/components/common";
import { useAdminAccess } from "@/context/AdminAccess";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Stars = ({ n }) => (
  <span className="inline-flex" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}
  </span>
);

const SENT = {
  Positive: "bg-emerald-50 text-emerald-700", Neutral: "bg-slate-100 text-slate-500", Negative: "bg-red-50 text-red-600",
};
const SentBadge = ({ s }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${SENT[s] || SENT.Neutral}`} data-testid={`cv-sentiment-${s}`}>
    {s === "Negative" && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}{s}
  </span>
);

export default function AdminCustomerVoice() {
  const { can } = useAdminAccess();
  const canReply = can("customer_voice.reply");
  const [d, setD] = useState(undefined);
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/customer-voice").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  if (d === undefined) return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (d === null) return <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="customer-voice-forbidden">You don't have access to Customer Voice.</div>;

  const dist = [5, 4, 3, 2, 1].map((n) => ({ date: `${n} ★`, Responses: d.distribution[String(n)] || 0 }));

  const openReply = (f) => { setSel(f); setReply(f.admin_reply || ""); };
  const sendReply = async () => {
    if (busy) return;
    if (!reply.trim()) { toast.error("Please enter a reply."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/feedback/${sel.id}/reply`, { reply: reply.trim(), status: "Resolved" });
      toast.success("Reply sent to the customer.");
      setSel(null); setReply(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const exportRecent = () => exportCsv(
    d.recent.map((f) => ({
      feedback_id: f.id, date: fmtDateTime(f.created_at), rating: f.rating, category: f.category,
      sentiment: f.sentiment, feedback: f.text || "", feature_request: f.feature_request || "",
      reply_status: f.status || "Open", device: f.device || "",
    })), "customer-voice-feedback");

  return (
    <div className="space-y-6" data-testid="customer-voice-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-[#0B132B]"><MessageSquareHeart size={22} className="text-[#14B8A6]" /> Customer Voice</h1>
          <p className="text-sm text-slate-500">Ratings, sentiment and feature requests from customers</p>
        </div>
        <button onClick={exportRecent} data-testid="cv-export" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:border-[#14B8A6]">
          <Download size={15} /> Export CSV
        </button>
      </div>

      {/* Weekly top emerging complaint — compact banner */}
      {d.top_complaint ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="cv-emerging-banner">
          <span className="flex items-center gap-2 font-display text-sm font-bold text-amber-800"><Flame size={16} className="text-amber-500" /> Top Emerging Complaint</span>
          <span className="text-sm font-semibold text-[#0B132B]">{d.top_complaint.theme}</span>
          <span className="text-sm text-slate-600">{d.top_complaint.count} mentions this week</span>
          {d.top_complaint.prev > 0
            ? <span className="text-xs font-semibold text-red-600">↑ {d.top_complaint.pct}% vs last week</span>
            : <span className="text-xs font-semibold text-amber-600">new this week</span>}
          {d.top_complaint.sample && <span className="w-full text-xs italic text-slate-500">“{d.top_complaint.sample}”</span>}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-400" data-testid="cv-emerging-empty">Not enough feedback data yet to surface an emerging complaint.</div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Total Feedback" value={d.total} icon={MessageSquareHeart} />
        <Kpi label="Average Rating" value={`${d.avg_rating} / 5`} icon={Star} accent="#F59E0B" />
        <Kpi label="Positive" value={d.sentiment_counts.positive} icon={ThumbsUp} hint={`${d.positive_pct}% of all`} accent="#10B981" />
        <Kpi label="Negative" value={d.sentiment_counts.negative} icon={ThumbsDown} hint="needs attention" accent="#EF4444" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Rating distribution" subtitle="Responses per star rating">
          <Bars data={dist} keys={["Responses"]} height={240} />
        </Panel>
        <Panel title="Feedback by category" subtitle="Most common themes">
          {d.by_category.length ? <Donut data={d.by_category} height={240} /> : <p className="py-16 text-center text-sm text-slate-400">No feedback yet.</p>}
        </Panel>
      </div>

      <Panel title="Feature requests" subtitle={`${d.feature_requests.length} suggestion(s)`}>
        {d.feature_requests.length ? (
          <div className="space-y-2">
            {d.feature_requests.map((r, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`cv-feature-${i}`}>
                <Lightbulb size={15} className="mt-0.5 shrink-0 text-amber-500" />
                <div className="min-w-0"><p className="text-sm text-[#0B132B]">{r.text}</p><p className="text-[11px] text-slate-400">{fmtDateTime(r.when)}</p></div>
              </div>
            ))}
          </div>
        ) : <p className="py-6 text-center text-sm text-slate-400">No feature requests yet.</p>}
      </Panel>

      <Panel title="Recent feedback" subtitle="Latest customer responses • click a row to reply">
        <RankTable
          cols={["Sentiment", "Rating", "Category", "Feedback", "Status", "Action"]}
          rows={d.recent}
          render={(f) => (
            <tr key={f.id} data-testid={`cv-row-${f.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openReply(f)}>
              <td className="px-4 py-3"><SentBadge s={f.sentiment} /></td>
              <td className="px-4 py-3"><Stars n={f.rating} /></td>
              <td className="whitespace-nowrap px-4 py-3 font-medium text-[#0B132B]">{f.category}</td>
              <td className="max-w-sm px-4 py-3 text-slate-600"><span className="line-clamp-1">{f.text || <span className="text-slate-300">—</span>}</span></td>
              <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${f.status === "Resolved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{f.status || "Open"}</span></td>
              <td className="px-4 py-3"><span className="text-xs font-semibold text-[#14B8A6]">{f.admin_reply ? "View" : "Reply"}</span></td>
            </tr>
          )}
        />
        {d.recent.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No feedback yet.</p>}
      </Panel>

      {/* Feedback detail + reply */}
      <Dialog open={!!sel} onOpenChange={(v) => { if (!v) { setSel(null); setReply(""); } }}>
        <DialogContent className="max-w-lg" data-testid="cv-reply-modal">
          {sel && (
            <div className="space-y-3">
              <DialogTitle className="font-display text-lg font-bold text-[#0B132B]">Customer Feedback</DialogTitle>
              <DialogDescription className="sr-only">Feedback detail and admin reply</DialogDescription>
              <div className="flex items-center gap-3"><Stars n={sel.rating} /><SentBadge s={sel.sentiment} /><span className="text-sm font-medium text-slate-500">{sel.category}</span></div>
              {sel.text && <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-[#0B132B]">{sel.text}</div>}
              {sel.feature_request && <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800"><span className="font-semibold">Feature request: </span>{sel.feature_request}</div>}
              <p className="text-[11px] text-slate-400">{fmtDateTime(sel.created_at)} • {sel.device || "device n/a"}</p>

              {sel.admin_reply && (
                <div className="rounded-lg border border-[#14B8A6]/30 bg-[#14B8A6]/5 px-3 py-2 text-sm">
                  <p className="text-[11px] font-semibold text-[#14B8A6]">Current reply</p>
                  <p className="text-[#0B132B]">{sel.admin_reply}</p>
                </div>
              )}

              {canReply ? (
                <div>
                  <label className="text-xs font-semibold text-[#0B132B]">Admin reply</label>
                  <textarea data-testid="cv-reply-text" rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
                    placeholder="Write a short response the customer will see…"
                    className="mt-1.5 w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
                  <button onClick={sendReply} disabled={busy} data-testid="cv-reply-send"
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B132B] disabled:opacity-60">
                    {busy ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><Send size={15} /> Send Reply & Resolve</>}
                  </button>
                </div>
              ) : <p className="text-xs text-slate-400">You don't have permission to reply to feedback.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
